import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Search, MapPin, UtensilsCrossed, Loader2, Phone, Globe } from "lucide-react";
import api from "../lib/api";
import { mediaUrl } from "../lib/mediaUrl";
import { Input } from "../components/ui/input";
import SEOHead from "../components/SEOHead";
import OpenFastBadge from "../components/OpenFastBadge";

export default function RestaurantsList() {
  const [partners, setPartners] = useState([]);
  const [directory, setDirectory] = useState([]);
  const [trendingIds, setTrendingIds] = useState(new Set());
  const [q, setQ] = useState("");
  const [city, setCity] = useState("");
  const [cuisine, setCuisine] = useState("");
  const [loadingP, setLoadingP] = useState(true);
  const [loadingD, setLoadingD] = useState(true);

  // Communes bruxelloises couvertes (Feature Filtre Ville)
  const COMMUNES = ["Ixelles", "Saint-Gilles", "Uccle", "Etterbeek", "Schaerbeek", "Anderlecht", "Bruxelles-Ville", "Woluwe"];
  // Familles de cuisines (Feature Filtre Cuisine Galerie)
  const CUISINES = ["Italien", "Africain", "Asiatique", "Belge", "Burger", "Végé"];

  useEffect(() => {
    let cancelled = false;
    const t = setTimeout(async () => {
      setLoadingP(true); setLoadingD(true);
      try {
        const cityParam = city ? `&city=${encodeURIComponent(city)}` : "";
        // La cuisine est envoyée dans q= (le backend fait déjà un regex insensitive sur cuisine)
        const searchQuery = cuisine ? (q ? `${q} ${cuisine}` : cuisine) : q;
        const [p, d, tr] = await Promise.all([
          api.get(`/restaurants?q=${encodeURIComponent(searchQuery)}${cityParam}&limit=48`).then(r => r.data),
          api.get(`/restaurants/directory?q=${encodeURIComponent(searchQuery)}&limit=60`).then(r => r.data),
          api.get(`/restaurants/trending?limit=3`).then(r => r.data).catch(() => ({ items: [] })),
        ]);
        if (cancelled) return;
        setPartners(p.items || (Array.isArray(p) ? p : []));
        setDirectory(d.items || []);
        setTrendingIds(new Set((tr.items || []).map((r) => r.id)));
      } catch (e) { console.warn("Restos fetch failed:", e); }
      finally { if (!cancelled) { setLoadingP(false); setLoadingD(false); } }
    }, 250);
    return () => { cancelled = true; clearTimeout(t); };
  }, [q, city, cuisine]);

  return (
    <div className="min-h-screen bg-[#FDF8F0]">
      <SEOHead
        title="Tous les restaurants à Bruxelles — Nold Go"
        description="Parcourez tous les restaurants bruxellois disponibles à la livraison : italien, africain, burgers, sushi, végé. Filtrez par commune et cuisine sur Nold Go."
        canonical="/restaurants"
        type="website"
      />
      <div className="max-w-6xl mx-auto px-4 sm:px-6 py-8">
        <div className="flex flex-col gap-3 mb-6">
          <p className="text-xs uppercase tracking-widest text-[#75807A]">Nold Go · Bruxelles</p>
          <h1 className="font-head text-3xl sm:text-4xl font-black text-[#1A1A1A]">Restaurants à Bruxelles</h1>
          <p className="text-sm text-[#3B423E] max-w-2xl">Découvrez tous les restaurants bruxellois. Nos partenaires livrent chez vous ; les autres sont en cours d'onboarding sur Nold Go.</p>
        </div>

        <div className="relative mb-4">
          <Search className="w-4 h-4 text-[#75807A] absolute left-3 top-1/2 -translate-y-1/2" />
          <Input data-testid="restos-search" value={q} onChange={(e) => setQ(e.target.value)}
            placeholder="Rechercher : cuisine, nom, quartier…"
            className="h-11 pl-9 rounded-xl border-[#E2DFD8] bg-white" />
        </div>

        {/* Filtre par commune — chips cliquables (feature Filtre Ville Galerie) */}
        <div className="mb-6 flex items-center gap-2 flex-wrap" data-testid="restos-city-filter">
          <button
            onClick={() => setCity("")}
            data-testid="restos-city-all"
            className={`h-8 px-3 rounded-full text-xs font-bold transition-all ${city === "" ? "bg-[#F26419] text-white" : "bg-white text-[#3B423E] border border-[#E2DFD8] hover:border-[#F26419]"}`}
          >
            Toutes
          </button>
          {COMMUNES.map((c) => (
            <button
              key={c}
              onClick={() => setCity(c)}
              data-testid={`restos-city-${c.toLowerCase().replace(/\s+/g, "-")}`}
              className={`h-8 px-3 rounded-full text-xs font-bold transition-all ${city === c ? "bg-[#F26419] text-white" : "bg-white text-[#3B423E] border border-[#E2DFD8] hover:border-[#F26419]"}`}
            >
              📍 {c}
            </button>
          ))}
        </div>

        {/* Filtre par cuisine — chips cliquables (feature Filtre Cuisine Galerie) */}
        <div className="mb-6 flex items-center gap-2 flex-wrap" data-testid="restos-cuisine-filter">
          <button
            onClick={() => setCuisine("")}
            data-testid="restos-cuisine-all"
            className={`h-8 px-3 rounded-full text-xs font-bold transition-all ${cuisine === "" ? "bg-[#059669] text-white" : "bg-white text-[#3B423E] border border-[#E2DFD8] hover:border-[#059669]"}`}
          >
            Toutes cuisines
          </button>
          {CUISINES.map((c) => (
            <button
              key={c}
              onClick={() => setCuisine(c)}
              data-testid={`restos-cuisine-${c.toLowerCase().replace(/\s+/g, "-")}`}
              className={`h-8 px-3 rounded-full text-xs font-bold transition-all ${cuisine === c ? "bg-[#059669] text-white" : "bg-white text-[#3B423E] border border-[#E2DFD8] hover:border-[#059669]"}`}
            >
              🍽️ {c}
            </button>
          ))}
        </div>

        {/* Partenaires actifs */}
        <div className="mb-3 flex items-center justify-between">
          <h2 className="font-head text-xl font-bold text-[#1A1A1A]">🍽️ Partenaires Nold Go <span className="ml-2 text-sm text-[#F26419] font-semibold">({partners.length})</span></h2>
          <span className="text-xs px-2 py-1 rounded-full bg-[#DCFCE7] text-[#166534] font-bold">LIVRAISON DISPONIBLE</span>
        </div>
        {loadingP ? (
          <div className="flex items-center justify-center py-10 text-[#75807A]"><Loader2 className="w-5 h-5 animate-spin mr-2" /> Chargement…</div>
        ) : partners.length === 0 ? (
          <div className="rounded-2xl border-2 border-dashed border-[#E2DFD8] p-8 text-center text-[#75807A] mb-8">Aucun partenaire ne correspond à votre recherche.</div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 mb-10">
            {partners.map((r) => (
              <Link key={r.id} to={`/restaurants/${r.id}`} data-testid={`resto-card-${r.id}`}
                className="group bg-white rounded-2xl border border-[#E2DFD8] overflow-hidden hover:border-[#F26419] hover:shadow-lg transition-all relative">
                {trendingIds.has(r.id) && (
                  <div data-testid={`resto-trending-${r.id}`}
                    className="absolute top-2 left-2 z-10 inline-flex items-center gap-1 rounded-full bg-gradient-to-br from-[#F26419] to-[#D94F2B] text-white px-2.5 py-1 text-[10px] font-black uppercase tracking-widest shadow-lg">
                    🔥 Tendance
                  </div>
                )}
                <div className="relative aspect-[16/9] bg-[#F0EBE1] overflow-hidden">
                  {r.cover_url ? (
                    <img src={mediaUrl(r.cover_url)} alt={r.name}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform" loading="lazy"
                      onError={(e) => { e.currentTarget.style.display = "none"; }} />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center text-[#75807A]"><UtensilsCrossed className="w-10 h-10" /></div>
                  )}
                  {/* Badge FOMO "Ouvert & rapide" en bas-droit (n'apparaît que si le resto est ouvert et livre en ≤ 30 min) */}
                  <div className="absolute bottom-2 right-2">
                    <OpenFastBadge resto={r} />
                  </div>
                </div>
                <div className="p-4">
                  <h3 className="font-head font-bold text-[#1A1A1A] text-lg truncate">{r.name}</h3>
                  {r.cuisine && <p className="text-xs text-[#F26419] font-semibold uppercase tracking-wider mt-0.5 truncate">{r.cuisine}</p>}
                  <div className="mt-2 flex items-center gap-1.5 text-xs text-[#75807A]">
                    <MapPin className="w-3.5 h-3.5 shrink-0" />
                    <span className="truncate">{r.address}{r.commune ? ` · ${r.commune}` : ""}</span>
                  </div>
                </div>
              </Link>
            ))}
          </div>
        )}

        {/* Annuaire OSM */}
        <div className="mb-3 flex items-center justify-between">
          <h2 className="font-head text-xl font-bold text-[#1A1A1A]">📇 Annuaire Bruxelles <span className="ml-2 text-sm text-[#75807A] font-semibold">({directory.length})</span></h2>
          <span className="text-xs px-2 py-1 rounded-full bg-[#FFF3E6] text-[#7A3A00] font-bold">BIENTÔT SUR NOLD GO</span>
        </div>
        <p className="text-xs text-[#75807A] mb-4">Ces restaurants sont référencés par OpenStreetMap. Ils n'ont pas encore rejoint Nold Go — nous les invitons à devenir partenaires pour que vous puissiez commander chez eux.</p>
        {loadingD ? (
          <div className="flex items-center justify-center py-10 text-[#75807A]"><Loader2 className="w-5 h-5 animate-spin mr-2" /> Chargement…</div>
        ) : directory.length === 0 ? (
          <div className="rounded-2xl border-2 border-dashed border-[#E2DFD8] p-8 text-center text-[#75807A]">Aucun restaurant dans l'annuaire pour cette recherche.</div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {directory.map((r) => (
              <div key={r.id} data-testid={`dir-card-${r.id}`}
                className="bg-white rounded-xl border border-[#E2DFD8] p-3 hover:border-[#F26419]/60 transition-all">
                <h3 className="font-head font-bold text-[#1A1A1A] text-sm truncate">{r.name}</h3>
                {r.cuisine && <p className="text-[10px] text-[#F26419] font-bold uppercase tracking-wider mt-0.5 truncate">{r.cuisine}</p>}
                <div className="mt-1.5 flex items-center gap-1 text-[11px] text-[#75807A]">
                  <MapPin className="w-3 h-3 shrink-0" />
                  <span className="truncate">{r.street ? `${r.street} · ` : ""}{r.commune || "Bruxelles"}</span>
                </div>
                <div className="mt-2 flex items-center gap-2 flex-wrap">
                  {r.phone && (
                    <a href={`tel:${r.phone}`} onClick={(e) => e.stopPropagation()}
                      className="inline-flex items-center gap-1 text-[10px] px-2 py-0.5 rounded-full bg-[#FFF7E8] text-[#7A3A00] hover:bg-[#FFEAC4]"><Phone className="w-3 h-3" /> Appeler</a>
                  )}
                  {r.website && (
                    <a href={r.website} target="_blank" rel="noreferrer" onClick={(e) => e.stopPropagation()}
                      className="inline-flex items-center gap-1 text-[10px] px-2 py-0.5 rounded-full bg-[#F0EBE1] text-[#3B423E] hover:bg-[#E2DFD8]"><Globe className="w-3 h-3" /> Site web</a>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
